import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { UserSessionAzure } from '../interfaces/azure/userAzure';
import { ConfigService } from './core/config.service';
import { Adherent } from '../interfaces/BDD-Ref/adherent';
export interface GpoRole {
  isResponsable: boolean;
  isTitulaire: boolean;
  isAdherent: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class UserSessionService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly selectedAdherentSubject: BehaviorSubject<Adherent | null> =
    new BehaviorSubject<Adherent | null>(null);
  selectedAdherent$: Observable<Adherent | null> = this.selectedAdherentSubject.asObservable();

  private static adherents: Adherent[] = [];

  private activiteSubject = new BehaviorSubject<any>(null);
  activite$ = this.activiteSubject.asObservable();

  private gpoRoleSubject = new BehaviorSubject<GpoRole>({
    isResponsable: false,
    isTitulaire: false,
    isAdherent: false,
  });

  gpoRole$ = this.gpoRoleSubject.asObservable();

  private filialeSubject = new BehaviorSubject<string | null>(null);

  filiale$ = this.filialeSubject.asObservable();

  setGpoRole(role: GpoRole): void {
    this.gpoRoleSubject.next(role);
  }

  setFiliale(filiale: string | null): void {
    this.filialeSubject.next(filiale);
  }

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {}

  setActivite(value: string) {
    this.activiteSubject.next(value);
  }

  getActivite(): string {
    return this.activiteSubject.value;
  }

  /**
   * Unsubscribes from all subscriptions to prevent memory leaks when the service is destroyed.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves user session from Azure by email address.
   * @param email The email address of the user.
   * @param accessToken The access token for authorization.
   * @returns Observable<UserSessionAzure>
   */
  getUserByEmail(email: string, accessToken: string): Observable<UserSessionAzure> {
    const url = `${this.configService.config.backendUri}/User/UserSessionAzure?email=${email}`;
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      }),
    };

    return this.http.get<UserSessionAzure>(url, httpOptions).pipe(
      takeUntil(this._destroying$),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  /**
   * Retrieves officine data for a given user and stores the adherents statically.
   * @param email The user's email.
   * @param type The page type (for logging purposes).
   * @returns Observable<Adherent[]>
   */
  public getOfficine(email: string, type: string): Observable<Adherent[]> {
    return this.http
      .get<
        Adherent[]
      >(`${this.configService.config.backendUri}/User/officines?email=${email}&type=${type}`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  public getOfficineGPO(
    email: string,
    type: string,
    filiale?: string | null,
  ): Observable<Adherent[]> {
    let params = new HttpParams().set('email', email).set('type', type);

    if (filiale) {
      params = params.set('filiale', filiale);
    }

    return this.http
      .get<Adherent[]>(`${this.configService.config.backendUri}/User/officinesGPO`, { params })
      .pipe(
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }
  /**
   * Stores the adherents for static retrieval.
   * Should be called once the adherents are retrieved.
   * @param adherents The list of adherents available for the user.
   */
  static setAdherents(adherents: Adherent[]): void {
    UserSessionService.adherents = adherents;
  }

  /**
   * Retrieves the list of idAdherent available for the connected user.
   * @returns number[]
   */
  static getAvailableAdherentIds(): number[] {
    return UserSessionService.adherents.map(adherent => adherent.idAdherent);
  }

  /**
   * Sets the currently selected adherent.
   * @param adherent The adherent to be selected.
   */
  setSelectedAdherent(adherent: Adherent | null): void {
    this.selectedAdherentSubject.next(adherent);
  }

  /**
   * Gets the currently selected adherent as an observable.
   * @returns Observable<Adherent | null>
   */
  getSelectedAdherent(): Observable<Adherent | null> {
    return this.selectedAdherent$;
  }
}
